<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-4xl mx-auto">
        <a href="/" class="text-emerald-200 hover:text-white text-sm font-medium">&larr; Back to courses</a>
        <h1 class="text-2xl font-bold mt-2">Vietnamese Alphabet & {{ accentSide }} Tones</h1>
        <p class="mt-1 text-emerald-100">Reference guide for pronunciation fundamentals</p>
      </div>
    </header>

    <main class="max-w-4xl mx-auto px-4 py-8 space-y-10">
      <!-- Tab switcher + dialect toggle -->
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex gap-2">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'px-4 py-2 rounded-lg font-medium text-sm transition-colors',
              activeTab === tab.id
                ? 'bg-emerald-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 dark:text-gray-300 hover:bg-gray-50'
            ]"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Only rendered once the Northern letter audio has been generated
             and committed (rake alphabet:north_guide_audio). -->
        <div v-if="northAvailable" class="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:bg-gray-800 dark:border-gray-700">
          <button
            v-for="d in dialectOptions"
            :key="d.id"
            @click="dialect = d.id"
            :class="[
              'px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
              dialect === d.id ? 'bg-emerald-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
            ]"
          >
            {{ d.label }}
          </button>
        </div>
      </div>

      <a
        v-if="practiceUrl"
        :href="practiceUrl"
        class="inline-flex w-fit items-center gap-2 bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
      >
        Practice the alphabet &rarr;
      </a>

      <!-- Alphabet Tab -->
      <section v-if="activeTab === 'alphabet'">
        <h2 class="text-xl font-semibold text-gray-800 mb-4 dark:text-gray-100">The Vietnamese Alphabet</h2>
        <p class="text-gray-600 mb-6 dark:text-gray-300">
          Vietnamese uses the Latin alphabet with 29 letters. It does not include F, J, W, or Z from the English alphabet,
          but adds 7 modified letters with diacritics. The letters are grouped into <strong>vowels</strong> and
          <strong>consonants</strong>: most consonants are close to their English sounds, so spend the bulk of your time on
          the vowels — that's where {{ accentSide }} Vietnamese pronunciation is won or lost.
        </p>

        <div v-for="group in letterGroups" :key="group.id" class="mb-10 last:mb-0">
          <div class="mb-4">
            <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">{{ group.title }}</h3>
            <p class="text-sm text-gray-500 mt-1 dark:text-gray-400">{{ group.blurb }}</p>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            <div
              v-for="letter in group.letters"
              :key="letter.char"
              class="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 hover:border-emerald-300 transition-colors dark:bg-gray-800 dark:border-gray-700"
              :class="letter.special ? 'ring-2 ring-emerald-100' : ''"
            >
              <span class="text-2xl font-bold text-gray-900 w-8 text-center dark:text-gray-50">{{ letter.char }}</span>
              <div class="flex-1 min-w-0 space-y-0.5">
                <p class="text-xs">
                  <span class="text-[10px] uppercase tracking-wide text-gray-400 mr-1 dark:text-gray-500">called</span>
                  <span class="text-gray-700 font-medium dark:text-gray-200">{{ letter.name }}</span>
                </p>
                <p class="text-xs">
                  <span class="text-[10px] uppercase tracking-wide text-gray-400 mr-1 dark:text-gray-500">sound</span>
                  <span class="text-gray-600 dark:text-gray-300">{{ approxFor(letter) }}</span>
                </p>
                <p class="text-xs">
                  <span class="text-[10px] uppercase tracking-wide text-gray-400 mr-1 dark:text-gray-500">example</span>
                  <span class="text-gray-700 font-medium dark:text-gray-200">{{ letter.example }}</span>
                </p>
              </div>
              <div class="flex flex-col gap-1.5 flex-shrink-0">
                <div class="flex flex-col items-center gap-0.5">
                  <AudioButton :src="audioSrc(letter.name)" :text="letter.name" />
                  <span class="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500">letter</span>
                </div>
                <div class="flex flex-col items-center gap-0.5">
                  <AudioButton :src="audioSrc(letter.sound)" :text="letter.sound" />
                  <span class="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500">sound</span>
                </div>
                <div class="flex flex-col items-center gap-0.5">
                  <AudioButton :src="audioSrc(letter.example)" :text="letter.example" />
                  <span class="text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500">word</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p class="text-sm text-gray-400 mt-4 dark:text-gray-500">
          <span class="font-medium text-gray-500 dark:text-gray-400">called</span> is the letter's spoken name when spelling aloud (like "aitch" for H);
          <span class="font-medium text-gray-500 dark:text-gray-400">sound</span> is what it makes in a word;
          <span class="font-medium text-gray-500 dark:text-gray-400">example</span> is a word that uses that sound. Each card has three speakers — the
          <span class="font-medium text-gray-500 dark:text-gray-400">letter</span> plays its spoken name, the <span class="font-medium text-gray-500 dark:text-gray-400">sound</span>
          plays the raw phonics sound, and the <span class="font-medium text-gray-500 dark:text-gray-400">word</span> plays the example.
          Letters with a green ring are unique to Vietnamese.
        </p>
      </section>

      <section v-if="activeTab === 'tones'">
        <h2 class="text-xl font-semibold text-gray-800 mb-4 dark:text-gray-100">{{ accentSide }} Vietnamese Tones</h2>
        <p v-if="dialect === 'north'" class="text-gray-600 mb-2 dark:text-gray-300">
          Vietnamese is a tonal language — the same syllable can mean completely different things depending on its tone.
          The Northern (Hanoi) dialect keeps all <strong>6 tones</strong> distinct: unlike in the South,
          <strong>h&#7887;i</strong> and <strong>ng&atilde;</strong> are different tones — ng&atilde; carries a glottal
          "break" in the middle of the syllable.
        </p>
        <p v-else class="text-gray-600 mb-2 dark:text-gray-300">
          Vietnamese is a tonal language — the same syllable can mean completely different things depending on its tone.
          Standard Vietnamese has 6 named tones, but in the Southern dialect <strong>h&#7887;i</strong> and <strong>ng&atilde;</strong>
          merge into one tone, giving <strong>5 distinct tones</strong> in practice.
        </p>
        <p class="text-gray-500 text-sm mb-6 dark:text-gray-400">
          Tap the speaker icon to hear each tone applied to the syllable "ma".
        </p>

        <div class="space-y-4">
          <div
            v-for="tone in tones"
            :key="tone.name"
            class="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700"
          >
            <div class="flex items-start gap-4">
              <div class="text-center flex-shrink-0 w-16">
                <span class="text-3xl font-bold text-gray-900 dark:text-gray-50">{{ tone.example }}</span>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <h3 class="font-semibold text-gray-800 dark:text-gray-100">{{ tone.name }}</h3>
                  <span v-if="tone.mark" class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded dark:bg-gray-800 dark:text-gray-400">{{ tone.mark }}</span>
                  <span v-else class="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded dark:bg-gray-800 dark:text-gray-400">no mark</span>
                  <AudioButton :src="tone.audio || audioSrc(tone.example)" :text="tone.example" />
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-300">{{ tone.description }}</p>
                <p v-if="tone.analogy" class="text-sm text-emerald-700 mt-1">
                  <span class="mr-1">💡</span>{{ tone.analogy }}
                </p>
                <p class="text-xs text-gray-400 mt-1 dark:text-gray-500">{{ tone.meaning }}</p>
                <div v-if="tone.merged" class="mt-2 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg inline-block">
                  In Southern Vietnamese, h&#7887;i and ng&atilde; sound the same — both are a low dipping tone.
                </div>
              </div>
              <!-- Tone contour visualization -->
              <div class="flex-shrink-0 w-16 h-12 flex items-end">
                <svg viewBox="0 0 60 40" class="w-full h-full text-emerald-500">
                  <path :d="tone.contour" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import AudioButton from './shared/AudioButton.vue'
import { apiPost } from '../api/client'

// When reached as the alphabet unit's lesson, a practice URL is passed so we can
// offer a link into that unit's exercises. Null when viewed as a standalone guide.
const props = defineProps({
  practiceUrl: { type: String, default: null },
  unitId: { type: Number, default: null },
  initialDialect: { type: String, default: 'south' },
  northAvailable: { type: Boolean, default: false },
})

const activeTab = ref('alphabet')

// The alphabet unit is the first thing a new account is sent to, and this guide
// used to record nothing at all — so reading it left no trace, the dashboard
// kept saying "Start · Unit 0", and the recommended-next-step spine never moved
// off it. Working through both tabs marks the unit LEARNED (not completed):
// that flips the dashboard CTA to "Practice to complete this unit", and unit 0
// then completes through its exercises like every other unit.
const seenTabs = new Set(['alphabet'])
const marked = ref(false)

watch(activeTab, async (tab) => {
  seenTabs.add(tab)
  if (marked.value || !props.unitId) return
  if (!tabs.value.every(t => seenTabs.has(t.id))) return

  marked.value = true
  try {
    await apiPost('/api/v1/progress/mark_learned', { unit_id: props.unitId })
  } catch (e) {
    // Progress is a nicety here — a failed mark must never break the guide.
    marked.value = false
  }
})

// Starts on the account-wide preference (header/Settings toggle); switching
// here is a per-visit override. Southern until the Northern letter audio has
// been generated — the letters would fall back to robotic browser speech.
const dialectOptions = [
  { id: 'south', label: 'Southern · Saigon' },
  { id: 'north', label: 'Northern · Hanoi' },
]
const dialect = ref(props.northAvailable ? props.initialDialect : 'south')
const accentSide = computed(() => dialect.value === 'north' ? 'Northern' : 'Southern')

// Pre-rendered VieNeu audio lives at public/audio/alphabet/<word>.wav (see
// `rake alphabet:guide_audio`), with the Northern render of the same words in
// an /north/ subtree (`rake alphabet:north_guide_audio`). Falling through to
// AudioButton's :text means a missing file still degrades to browser speech
// rather than going silent.
function audioSrc(word) {
  const dir = dialect.value === 'north' ? '/audio/alphabet/north' : '/audio/alphabet'
  return `${dir}/${encodeURIComponent(word)}.wav`
}

// A letter's spoken sound differs by dialect for a handful of consonants —
// `approxNorth` overrides the display, the audio difference comes from the
// dialect's own audio tree.
function approxFor(letter) {
  return dialect.value === 'north' ? letter.approxNorth || letter.approx : letter.approx
}

const tabs = computed(() => [
  { id: 'alphabet', label: 'Alphabet (29 letters)' },
  { id: 'tones', label: dialect.value === 'north' ? 'Northern Tones (6)' : 'Southern Tones (5)' },
])

// `sound` is the letter's phonics value (Vietnamese "âm") as a pronounceable
// syllable: bare vowels, and consonants voiced as consonant + "ờ" (bờ, cờ, dờ…),
// which is how Vietnamese phonics isolates each consonant's sound. Kept in sync
// with GUIDE_SOUNDS in lib/tasks/alphabet_unit.rake.
//
// Letters are split into vowels and consonants: the vowels carry the difficulty in
// Southern Vietnamese (many have no English equivalent), while the consonants are
// mostly close to their English sounds. Each example word must actually use the
// letter's sound — e.g. O ("aw") uses "to", whereas "ông" belongs to Ô ("oh").
const vowels = [
  { char: 'A', name: 'a', approx: 'ah', sound: 'a', example: 'ba' },
  { char: 'Ă', name: 'á', approx: 'short a', sound: 'ă', example: 'ăn', special: true },
  { char: 'Â', name: 'ớ', approx: 'uh', sound: 'â', example: 'ân', special: true },
  { char: 'E', name: 'e', approx: 'eh', sound: 'e', example: 'em' },
  { char: 'Ê', name: 'ê', approx: 'ay', sound: 'ê', example: 'tê', special: true },
  { char: 'I', name: 'i', approx: 'ee', sound: 'i', example: 'im' },
  { char: 'O', name: 'o', approx: 'aw', sound: 'o', example: 'to' },
  { char: 'Ô', name: 'ô', approx: 'oh', sound: 'ô', example: 'ông', special: true },
  { char: 'Ơ', name: 'ơ', approx: 'uh (open)', sound: 'ơ', example: 'ơi', special: true },
  { char: 'U', name: 'u', approx: 'oo', sound: 'u', example: 'ung' },
  { char: 'Ư', name: 'ư', approx: 'ew', sound: 'ư', example: 'ưng', special: true },
  { char: 'Y', name: 'i dài', approx: 'ee', sound: 'i', example: 'yêu' },
]

const consonants = [
  { char: 'B', name: 'bê', approx: 'b', sound: 'bờ', example: 'ba' },
  { char: 'C', name: 'xê', approx: 'k', sound: 'cờ', example: 'ca' },
  { char: 'D', name: 'dê', approx: 'y (south)', approxNorth: 'z (north)', sound: 'dờ', example: 'dao' },
  { char: 'Đ', name: 'đê', approx: 'd', sound: 'đờ', example: 'đi', special: true },
  { char: 'G', name: 'giê', approx: 'g', sound: 'gờ', example: 'gà' },
  { char: 'H', name: 'hát', approx: 'h', sound: 'hờ', example: 'hoa' },
  { char: 'K', name: 'ca', approx: 'k', sound: 'cờ', example: 'kẻ' },
  { char: 'L', name: 'e-lờ', approx: 'l', sound: 'lờ', example: 'lá' },
  { char: 'M', name: 'em-mờ', approx: 'm', sound: 'mờ', example: 'mẹ' },
  { char: 'N', name: 'en-nờ', approx: 'n', sound: 'nờ', example: 'nước' },
  { char: 'P', name: 'pê', approx: 'p', sound: 'pờ', example: 'pin' },
  { char: 'Q', name: 'quy', approx: 'kw', sound: 'quờ', example: 'quê' },
  { char: 'R', name: 'e-rờ', approx: 'r', approxNorth: 'z (north)', sound: 'rờ', example: 'ra' },
  { char: 'S', name: 'ét-sì', approx: 'sh (south)', approxNorth: 's (north, same as X)', sound: 'sờ', example: 'số' },
  { char: 'T', name: 'tê', approx: 't', sound: 'tờ', example: 'tôi' },
  { char: 'V', name: 'vê', approx: 'v (south: y)', approxNorth: 'v', sound: 'vờ', example: 'và' },
  { char: 'X', name: 'ích-xì', approx: 's', sound: 'xờ', example: 'xin' },
]

const letterGroups = computed(() => [
  {
    id: 'vowels',
    title: `Vowels (${vowels.length})`,
    blurb: 'The hard part. Several of these have no English equivalent and the differences between them (a / ă / â, o / ô / ơ, u / ư) are what make or break your accent — give these the most practice.',
    letters: vowels,
  },
  {
    id: 'consonants',
    title: `Consonants (${consonants.length})`,
    blurb: dialect.value === 'north'
      ? 'Mostly familiar — most map closely to their English sounds. Focus on the handful that differ in the North: D and R (both like English z), S (merges with X as plain s), plus Đ, which is a separate letter from D.'
      : 'Mostly familiar — most map closely to their English sounds. Focus on the handful that differ in the South: D and V (like English y), R, and S (like sh), plus Đ, which is a separate letter from D.',
    letters: consonants,
  },
])

const tones = computed(() => dialect.value === 'north' ? northTones : southTones)

const southTones = [
  {
    name: 'Ngang (level)',
    mark: null,
    example: 'ma',
    description: 'Mid-level pitch, flat and steady. Like speaking in a neutral, even voice.',
    analogy: 'Like humming a single, even note; the pitch holds steady the whole way through.',
    meaning: '"ma" = ghost',
    contour: 'M5,20 L55,20',
  },
  {
    name: 'Sắc (rising)',
    mark: '´ (acute accent)',
    example: 'má',
    description: 'Starts mid and rises sharply to high pitch. Like asking a surprised question.',
    analogy: 'Like the way your voice climbs upward at the end of a short question, such as "ready?".',
    meaning: '"má" = mother / cheek',
    contour: 'M5,30 L55,8',
  },
  {
    name: 'Huyền (falling)',
    mark: '` (grave accent)',
    example: 'mà',
    description: 'Starts mid and falls to a low pitch. Like a sighing or trailing-off voice.',
    analogy: 'Like the soft "ohh" that sinks downward when you realize you forgot something.',
    meaning: '"mà" = but / which',
    contour: 'M5,12 L55,32',
  },
  {
    name: 'Hỏi / Ngã (dipping)',
    mark: '̉  or  ̃ (hook or tilde)',
    example: 'mả',
    description: 'Dips low then rises back up — a "valley" shape. In Southern Vietnamese, hỏi and ngã merge into this same dipping tone.',
    analogy: 'Like a drawn-out "sooo...?" that dips down low before curling back up, the way you sound when you\'re unsure.',
    meaning: '"mả" = tomb, "mã" = horse (same tone in South)',
    merged: true,
    contour: 'M5,15 Q30,38 55,10',
  },
  {
    name: 'Nặng (heavy)',
    mark: '. (dot below)',
    example: 'mạ',
    description: 'Low, short, and cut off abruptly — like a glottal stop. The voice drops and stops suddenly.',
    analogy: 'Like a clipped "yep!" that you snap off the instant it leaves your mouth.',
    meaning: '"mạ" = rice seedling',
    contour: 'M5,12 L35,35 L38,35',
  },
]

// The Hanoi 6-tone system — hỏi and ngã are separate tones here. Copy kept in
// step with NORTH_ITEMS in the public tone trainer, and `audio` reuses that
// tool's committed, contour-verified Tuyen clips (same "ma" syllables) instead
// of the Southern alphabet tree.
const northTones = [
  {
    name: 'Ngang (level)',
    mark: null,
    example: 'ma',
    description: 'High-mid and steady, like reciting a list. Don\'t let it drift down.',
    analogy: 'Like humming a single, even note; the pitch holds steady the whole way through.',
    meaning: '"ma" = ghost',
    contour: 'M5,18 L55,18',
    audio: '/audio/tone_trainer/north/ngang_syllable.wav',
  },
  {
    name: 'Sắc (rising)',
    mark: '´ (acute accent)',
    example: 'má',
    description: 'Starts mid and climbs sharply, like the end of an English question: "yes?"',
    analogy: 'Like the way your voice climbs upward at the end of a short question, such as "ready?".',
    meaning: '"má" = cheek',
    contour: 'M5,30 L55,8',
    audio: '/audio/tone_trainer/north/sac_syllable.wav',
  },
  {
    name: 'Huyền (falling)',
    mark: '` (grave accent)',
    example: 'mà',
    description: 'Starts low and glides down further, relaxed and breathy — like a disappointed "oh."',
    analogy: 'Like the soft "ohh" that sinks downward when you realize you forgot something.',
    meaning: '"mà" = but / which',
    contour: 'M5,12 L55,32',
    audio: '/audio/tone_trainer/north/huyen_syllable.wav',
  },
  {
    name: 'Hỏi (dipping)',
    mark: '̉ (hook above)',
    example: 'mả',
    description: 'Dips down low and curls back up — a valley. In Hanoi this stays separate from ngã.',
    analogy: 'Like a drawn-out "sooo...?" that dips down low before curling back up.',
    meaning: '"mả" = tomb',
    contour: 'M5,15 Q30,38 55,10',
    audio: '/audio/tone_trainer/north/hoi_syllable.wav',
  },
  {
    name: 'Ngã (broken rise)',
    mark: '˜ (tilde)',
    example: 'mã',
    description: 'Rises, but catches in the throat halfway — a tiny break, then a jump up high. The break IS the tone.',
    analogy: 'Like "uh-oh" squeezed into one syllable: a catch in the throat, then a jump upward.',
    meaning: '"mã" = horse (in compounds)',
    contour: 'M5,26 L26,20 M34,14 L55,5',
    audio: '/audio/tone_trainer/north/nga_syllable.wav',
  },
  {
    name: 'Nặng (heavy)',
    mark: '. (dot below)',
    example: 'mạ',
    description: 'Drops fast and stops dead, cut off in the throat. Heavy and abrupt.',
    analogy: 'Like a clipped "yep!" that you snap off the instant it leaves your mouth.',
    meaning: '"mạ" = rice seedling',
    contour: 'M5,12 L35,35 L38,35',
    audio: '/audio/tone_trainer/north/nang_syllable.wav',
  },
]
</script>
